// ═══════════════════════════════════════════════════════════════
// Incident Detail — Command Hub
// Icon strip · Module panel flip · Live stats · Leaflet map
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import Hls from 'hls.js';
import AutoRefreshImage from '../../components/map/AutoRefreshImage';
import { fetchCameraStream } from '../../utils/perimeterCameras';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconArrowLeft, IconAlertTriangle, IconPencil, IconTrash,
  IconCloudStorm, IconFlame, IconDroplet, IconTornado, IconBuildingBridge,
  IconShield, IconMap, IconExternalLink,
  IconUsers, IconMapPin, IconClock, IconHomePlus,
  IconBackhoe, IconArrowsExchange, IconUserPlus, IconCalendarEvent,
  IconClipboardCheck, IconMap2, IconHomeExclamation, IconFileInvoice,
  IconActivity, IconRefresh, IconCheck, IconDots,
  IconLayoutDashboard, IconUsersGroup, IconBuildingStore, IconRadio, IconCamera,
  IconPresentation, IconPlayerPlay, IconPlayerPause,
  IconChevronLeft, IconChevronRight, IconX,
} from '@tabler/icons-react';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, CircleMarker, GeoJSON as LeafletGeoJSON, Popup, Tooltip, useMap } from 'react-leaflet';
import type { FeatureCollection } from 'geojson';
import L from 'leaflet';
import { fetchCountyBoundaries, type CountyFeatureCollection } from '../../lib/censusGeo';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { useAuthStore } from '../../store/authStore';
import { useIncidentStore } from '../../store/incidentStore';
import { useGeoOpsStore, ZONE_TYPE_META } from '../../store/geoopsStore';
import type { GeoZone } from '../../store/geoopsStore';
import { INCIDENT_TYPE_META } from '../../types';
import type { IncidentStatus } from '../../types';
import { notifications } from '../../lib/notifications';
import './IncidentDetail.css';

// ── Lazy module dashboards ────────────────────────────────────
const ImpactDashboard     = lazy(() => import('../impact/ImpactDashboard'));
const IntakeDashboard     = lazy(() => import('../intake/IntakeDashboard'));
const ShelterDashboard    = lazy(() => import('../shelter/ShelterDashboard'));
const DebrisDashboard     = lazy(() => import('../debris/DebrisDashboard'));
const VolunteerDashboard  = lazy(() => import('../volunteer/Dashboard'));
const GeoOpsDashboard     = lazy(() => import('../geoops/GeoOpsDashboard'));
const PAGrantsDashboard   = lazy(() => import('../pagrants/PAGrantsDashboard'));
const MainStreetDashboard = lazy(() => import('../mainstreet/Dashboard'));
const RollCallDashboard   = lazy(() => import('../rollcall/RollCallDashboard'));
const PACEDashboard       = lazy(() => import('../pace/PACEDashboard'));
const BRDashboard         = lazy(() => import('../battlerhythm/BRSchedule'));
const HandoffDashboard    = lazy(() => import('../handoff/HandoffDashboard'));

// ── Module panel registry ─────────────────────────────────────
interface PanelDef {
  id: string;
  name: string;
  Icon: React.ComponentType<{ size?: number }>;
  Component: React.LazyExoticComponent<React.ComponentType>;
}

const RESPOND_PANELS: PanelDef[] = [
  { id: 'impact',       name: 'Impact',      Icon: IconHomeExclamation, Component: ImpactDashboard },
  { id: 'intake',       name: 'Intake',       Icon: IconUserPlus,        Component: IntakeDashboard },
  { id: 'shelter',      name: 'Shelter',      Icon: IconHomePlus,         Component: ShelterDashboard },
  { id: 'debris',       name: 'Debris',       Icon: IconBackhoe,          Component: DebrisDashboard },
  { id: 'volunteer',    name: 'Volunteer',    Icon: IconUsersGroup,       Component: VolunteerDashboard },
  { id: 'geoops',       name: 'GeoOps',       Icon: IconMap2,             Component: GeoOpsDashboard },
];

const RECOVER_PANELS: PanelDef[] = [
  { id: 'pagrants',     name: 'PA Grants',   Icon: IconFileInvoice,      Component: PAGrantsDashboard },
  { id: 'mainstreet',   name: 'MainStreet',  Icon: IconBuildingStore,    Component: MainStreetDashboard },
  { id: 'rollcall',     name: 'RollCall',    Icon: IconClipboardCheck,   Component: RollCallDashboard },
  { id: 'pace',         name: 'PACE',         Icon: IconRadio,            Component: PACEDashboard },
  { id: 'battlerhythm', name: 'Br. Rhythm',  Icon: IconCalendarEvent,    Component: BRDashboard },
  { id: 'handoff',      name: 'Handoff',     Icon: IconArrowsExchange,   Component: HandoffDashboard },
];

const ALL_PANELS = [...RESPOND_PANELS, ...RECOVER_PANELS];

// Tabs cycled in display/EOC mode (Overview first, then all module panels)
const ALL_DISPLAY_TABS: { id: string | null; name: string }[] = [
  { id: null, name: 'Overview' },
  ...ALL_PANELS.map(p => ({ id: p.id, name: p.name })),
];

// ── Helpers ───────────────────────────────────────────────────

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#D33423',
  high:     '#d97706',
  medium:   '#d97706',
  low:      '#037730',
};

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  IconCloudStorm, IconFlame, IconDroplet, IconTornado,
  IconBuildingBridge, IconShield, IconAlertTriangle,
};

const MODULE_COLORS: Record<string, string> = {
  shelter: '#0EA5E9', impact: '#f59e0b', intake: '#8b5cf6',
  handoff: '#4B3FFF', rollcall: '#10b981', geoops: '#0EA5E9',
  debris: '#f97316', volunteer: '#ec4899', battlerhythm: '#6366f1',
  hotwash: '#14b8a6', timelabor: '#84cc16', workflows: '#a855f7',
  system: '#939598',
};

function elapsed(startDate?: string): string {
  if (!startDate) return '';
  const ms = Date.now() - new Date(startDate).getTime();
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `T+${d}d ${h}h ${m}m`;
  if (h > 0) return `T+${h}h ${m}m`;
  return `T+${m}m`;
}

function formatTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
}

function initials(name: string): string {
  return name.trim().split(/\s+/).map(p => p[0]?.toUpperCase() || '').join('').slice(0, 2) || '?';
}


// ── Data types ────────────────────────────────────────────────

interface Stats {
  shelters: number; population: number; capacity: number;
  assessments: number; assessmentsReview: number;
  debrisLoads: number; debrisOutstanding: number;
  volunteers: number; handoffEvents: number;
  loaded: boolean;
}

interface LogEntry {
  id: number; created_at: string; actorName: string;
  module: string; action: string; details: string;
}

// ── Tile layer configs ────────────────────────────────────────
const TILE_CONFIGS = {
  street:    { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',                                                                 label: 'Street' },
  satellite: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',                     label: 'Sat'    },
  topo:      { url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',                                                                   label: 'Topo'   },
} as const;
type TileView = keyof typeof TILE_CONFIGS;

// Captures the Leaflet map instance via useMap() so siblings outside MapContainer can call zoom methods
function MapRefCapture({ ref_ }: { ref_: React.MutableRefObject<L.Map | null> }) {
  const map = useMap();
  useEffect(() => { ref_.current = map; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// ── FitBounds — auto-zooms map to a GeoJSON layer ──────────────
function FitBounds({ geojson }: { geojson: CountyFeatureCollection | null }) {
  const map = useMap();
  useEffect(() => {
    if (!geojson || geojson.features.length === 0) return;
    try {
      const layer = L.geoJSON(geojson as any);
      const bounds = layer.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 10 });
    } catch { /* ignore — map stays at default */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geojson]);
  return null;
}

// ── Incident map (react-leaflet) ──────────────────────────────

function IncidentMap({ lat, lng, zones, assessPins, showAssessPins, stateAbbr, countyNames }: {
  lat: number | null; lng: number | null; zones: GeoZone[];
  assessPins: AssessPin[]; showAssessPins: boolean;
  stateAbbr?: string; countyNames?: string[];
}) {
  const center: [number, number] = lat != null && lng != null ? [lat, lng] : [39.5, -98.35];
  const zoom = lat != null && lng != null ? 12 : 4;
  const [mapView, setMapView] = useState<TileView>('street');
  const mapRef = useRef<L.Map | null>(null);

  const [countyGeo, setCountyGeo] = useState<CountyFeatureCollection | null>(null);
  useEffect(() => {
    if (!stateAbbr || !countyNames?.length) { setCountyGeo(null); return; }
    fetchCountyBoundaries(stateAbbr, countyNames)
      .then(setCountyGeo)
      .catch(() => setCountyGeo(null));
  }, [stateAbbr, countyNames?.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const featureCollection: FeatureCollection = useMemo(() => ({
    type: 'FeatureCollection',
    features: zones
      .filter(z => z.geometry != null)
      .map(z => ({
        type: 'Feature' as const,
        geometry: z.geometry,
        properties: {
          name:      z.name,
          typeLabel: ZONE_TYPE_META[z.zoneType]?.label || z.zoneType,
          color:     z.color || ZONE_TYPE_META[z.zoneType]?.color || '#6B7280',
        },
      })),
  }), [zones]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
    <MapContainer
      center={center}
      zoom={zoom}
      style={{ width: '100%', height: '100%' }}
      zoomControl={false}
      scrollWheelZoom={false}
      attributionControl={false}
    >
      <MapRefCapture ref_={mapRef} />
      <TileLayer key={mapView} url={TILE_CONFIGS[mapView].url} />
      {/* County boundary highlight */}
      {countyGeo && countyGeo.features.length > 0 && (
        <LeafletGeoJSON
          key={countyGeo.features.map(f => f.properties.COUNTY).join(',')}
          data={countyGeo as unknown as FeatureCollection}
          style={() => ({
            color: '#4B3FFF',
            fillColor: '#4B3FFF',
            fillOpacity: 0.08,
            weight: 2,
            dashArray: '4 3',
          })}
          onEachFeature={(feature, layer) => {
            layer.bindTooltip(
              `<div class="id2-map-tip"><div class="id2-map-tip-name">${feature.properties.NAME}</div></div>`,
              { sticky: true, direction: 'top', offset: [0, -4], className: 'id2-map-tip-wrap' },
            );
          }}
        />
      )}
      <FitBounds geojson={countyNames?.length ? countyGeo : null} />
      {lat != null && lng != null && (
        <CircleMarker
          center={[lat, lng]}
          radius={8}
          pathOptions={{ color: '#4B3FFF', fillColor: '#4B3FFF', fillOpacity: 0.85, weight: 2 }}
        >
          <Popup>Incident location</Popup>
        </CircleMarker>
      )}
      {featureCollection.features.length > 0 && (
        <LeafletGeoJSON
          key={featureCollection.features.length}
          data={featureCollection}
          style={(f) => ({
            color:       f?.properties?.color || '#4B3FFF',
            fillColor:   f?.properties?.color || '#4B3FFF',
            fillOpacity: 0.18,
            weight:      2,
          })}
          onEachFeature={(feature, layer) => {
            const { name, typeLabel, color } = feature.properties || {};
            if (name) {
              layer.bindTooltip(
                `<div class="id2-map-tip">
                   <span class="id2-map-tip-dot" style="background:${color}"></span>
                   <div>
                     <div class="id2-map-tip-name">${name}</div>
                     <div class="id2-map-tip-type">${typeLabel || ''}</div>
                   </div>
                 </div>`,
                { sticky: true, direction: 'top', offset: [0, -4], className: 'id2-map-tip-wrap' }
              );
            }
          }}
        />
      )}

      {/* Assessment pins */}
      {showAssessPins && assessPins.map(pin => {
        const col = damageColor(pin.damageCategory);
        const label = pin.damageCategory
          ? pin.damageCategory.charAt(0).toUpperCase() + pin.damageCategory.slice(1)
          : 'Assessed';
        return (
          <CircleMarker
            key={pin.id}
            center={[pin.lat, pin.lng]}
            radius={6}
            pathOptions={{ color: 'white', fillColor: col, fillOpacity: 0.9, weight: 1.5 }}
          >
            <Tooltip sticky direction="top" className="id2-map-tip-wrap">
              <div className="id2-map-tip">
                <span className="id2-map-tip-dot" style={{ background: col }} />
                <div>
                  <div className="id2-map-tip-name">
                    {[pin.address, pin.city].filter(Boolean).join(', ') || 'Assessment'}
                  </div>
                  <div className="id2-map-tip-type">{label}</div>
                </div>
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}
    </MapContainer>

    {/* Zoom +/- — top-right, stacked below "Open in GeoOps" */}
    <div className="id2-map-zoom">
      <button className="id2-map-zoom-btn" onClick={() => mapRef.current?.zoomIn()} title="Zoom in">+</button>
      <button className="id2-map-zoom-btn" onClick={() => mapRef.current?.zoomOut()} title="Zoom out">−</button>
    </div>

    {/* Map view switcher — bottom-right */}
    <div className="id2-map-view-sw">
      {(Object.keys(TILE_CONFIGS) as TileView[]).map(v => (
        <button
          key={v}
          className={`id2-map-view-btn${mapView === v ? ' active' : ''}`}
          onClick={() => setMapView(v)}
        >
          {TILE_CONFIGS[v].label}
        </button>
      ))}
    </div>
    </div>
  );
}

// ── Geo / weather helpers ─────────────────────────────────────

function wmoLabel(code: number): { label: string; emoji: string } {
  if (code === 0)              return { label: 'Clear',           emoji: '☀️' };
  if (code <= 2)               return { label: 'Partly cloudy',   emoji: '⛅' };
  if (code === 3)              return { label: 'Overcast',        emoji: '☁️' };
  if (code <= 48)              return { label: 'Fog',             emoji: '🌫️' };
  if (code <= 55)              return { label: 'Drizzle',         emoji: '🌦️' };
  if (code <= 65)              return { label: 'Rain',            emoji: '🌧️' };
  if (code <= 77)              return { label: 'Snow',            emoji: '❄️' };
  if (code <= 82)              return { label: 'Rain showers',    emoji: '🌦️' };
  if (code <= 86)              return { label: 'Snow showers',    emoji: '🌨️' };
  if (code <= 99)              return { label: 'Thunderstorm',    emoji: '⛈️' };
  return                              { label: 'Unknown',         emoji: '🌡️' };
}

function windCardinal(deg: number): string {
  const dirs = ['N','NE','E','SE','S','SW','W','NW'];
  return dirs[Math.round(deg / 45) % 8];
}

function daylightStatus(sunrise: string, sunset: string): string {
  const now = Date.now();
  const rise = new Date(sunrise).getTime();
  const set  = new Date(sunset).getTime();
  if (now < rise) {
    const m = Math.round((rise - now) / 60000);
    return `Sunrise in ${m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`}`;
  }
  if (now < set) {
    const m = Math.round((set - now) / 60000);
    return `Sunset in ${m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`}`;
  }
  return `Sun set ${new Date(sunset).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

interface AssessPin {
  id: number; lat: number; lng: number;
  address: string; city: string; damageCategory: string;
}

const DAMAGE_COLORS: Record<string, string> = {
  affected:     '#16a34a',
  minor:        '#d97706',
  major:        '#ea580c',
  destroyed:    '#B12525',
  inaccessible: '#7c3aed',
};
function damageColor(cat: string) { return DAMAGE_COLORS[cat?.toLowerCase()] || '#939598'; }

interface GeoContext { county?: string; city?: string; state?: string; }
interface WeatherSnap {
  tempF: number; code: number;
  windMph: number; windDeg: number;
  sunrise: string; sunset: string;
}

// ── Sub-components ────────────────────────────────────────────

function useCountUp(target: number, duration = 750): number {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    fromRef.current = target;
    if (from === target) return;
    const start = performance.now();
    function tick(now: number) {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }, [target, duration]);
  return display;
}

function StatCell({ label, value, sub, loading, color, Icon }: {
  label: string; value: string | number; sub?: string; loading?: boolean;
  color?: string; Icon?: React.ComponentType<{ size?: number; color?: string }>;
}) {
  const c = color || '#4B3FFF';
  const numericValue = typeof value === 'number' ? value : parseInt(String(value), 10);
  const isNumeric = !isNaN(numericValue);
  const animated = useCountUp(isNumeric && !loading ? numericValue : 0);
  return (
    <div className="id2-stat-cell" style={{ '--stat-color': c } as React.CSSProperties}>
      {Icon && <div className="id2-stat-icon"><Icon size={18} color={c} /></div>}
      {loading
        ? <div className="id2-stat-skel" />
        : <div className="id2-stat-value" style={{ color: c }}>{isNumeric ? animated : value}</div>
      }
      <div className="id2-stat-label">{label}</div>
      {sub && !loading && <div className="id2-stat-sub">{sub}</div>}
    </div>
  );
}

// ── Live Camera Feed (single panel) ──────────────────────────────
type CamEntry = ReturnType<typeof buildCamList>[0];

function buildCamList(zones: GeoZone[]) {
  return zones.flatMap(z =>
    (z.cameras || []).map(c => ({ ...c, zoneName: z.name, zoneColor: z.color }))
  );
}

function pickTwo<T>(arr: T[]): [T, T] | [T] | [] {
  if (arr.length === 0) return [];
  if (arr.length === 1) return [arr[0]];
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return [shuffled[0], shuffled[1]];
}

// Compare two canvas snapshots at 32×18 — returns average pixel delta per channel
function framesDiffer(a: ImageData, b: ImageData): number {
  let diff = 0;
  for (let i = 0; i < a.data.length; i += 4) {
    diff += Math.abs(a.data[i]   - b.data[i]);
    diff += Math.abs(a.data[i+1] - b.data[i+1]);
    diff += Math.abs(a.data[i+2] - b.data[i+2]);
  }
  return diff / (a.data.length / 4);
}

function LiveCameraPanel({ cam, onFrozen }: { cam: CamEntry; onFrozen: () => void }) {
  const videoRef  = useRef<HTMLVideoElement>(null);
  const hlsRef    = useRef<Hls | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mode, setMode]         = useState<'loading' | 'video' | 'snapshot' | 'none'>('loading');
  const [streamUrl, setStreamUrl] = useState<string | null>(null);

  // Fetch stream URL on mount and refresh every 90s
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const url = await fetchCameraStream(cam as any);
      if (!cancelled) setStreamUrl(url);
    }
    load();
    const refresh = setInterval(load, 90_000);
    return () => { cancelled = true; clearInterval(refresh); };
  }, [cam.id, cam.streamUrl]);

  // Wire up HLS when streamUrl is available
  useEffect(() => {
    if (streamUrl === null) {
      setMode(cam.imageUrl ? 'snapshot' : 'none');
      return;
    }
    if (!videoRef.current) return;
    const video = videoRef.current;
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }

    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: false, lowLatencyMode: true });
      hlsRef.current = hls;
      hls.loadSource(streamUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setMode('video');
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          hlsRef.current?.destroy(); hlsRef.current = null;
          setMode(cam.imageUrl ? 'snapshot' : 'none');
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = streamUrl;
      video.addEventListener('loadedmetadata', () => {
        setMode('video');
        video.play().catch(() => {});
      });
    } else {
      setMode(cam.imageUrl ? 'snapshot' : 'none');
    }

    return () => { hlsRef.current?.destroy(); hlsRef.current = null; };
  }, [streamUrl]);

  // Frozen-frame detection: sample two frames 2s apart after video starts.
  // If pixel delta < threshold the stream is a static "not available" screen — skip it.
  useEffect(() => {
    if (mode !== 'video') return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const W = 32, H = 18;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    let t1: ReturnType<typeof setTimeout>;
    let t2: ReturnType<typeof setTimeout>;

    // Wait 2s for stream to stabilise, then capture frame 1
    t1 = setTimeout(() => {
      try {
        ctx.drawImage(video, 0, 0, W, H);
        const frame1 = ctx.getImageData(0, 0, W, H);
        // 2s later, capture frame 2 and compare
        t2 = setTimeout(() => {
          try {
            ctx.drawImage(video, 0, 0, W, H);
            const frame2 = ctx.getImageData(0, 0, W, H);
            if (framesDiffer(frame1, frame2) < 3) {
              // Frozen/static stream — tell parent to swap this camera out
              hlsRef.current?.destroy(); hlsRef.current = null;
              onFrozen();
            }
          } catch { /* cross-origin canvas block — leave stream as-is */ }
        }, 2000);
      } catch { /* cross-origin canvas block — leave stream as-is */ }
    }, 2000);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [mode, onFrozen]);

  return (
    <div style={{ position: 'relative', background: '#0a0a0a', borderRadius: 4, overflow: 'hidden', aspectRatio: '16/9', flex: 1, minWidth: 0 }}>
      {/* Hidden canvas for frozen-frame detection */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      {/* HLS video (always rendered, hidden when not in video mode) */}
      <video
        ref={videoRef}
        muted playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: mode === 'video' ? 'block' : 'none' }}
      />

      {/* Snapshot fallback */}
      {mode === 'snapshot' && cam.imageUrl && (
        <AutoRefreshImage
          src={cam.imageUrl}
          alt={cam.name}
          intervalMs={15_000}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 0 }}
        />
      )}

      {/* Loading spinner */}
      {mode === 'loading' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 24, height: 24, border: '2px solid #333', borderTopColor: '#4B3FFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      )}

      {/* No feed */}
      {mode === 'none' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <IconCamera size={20} color="#444" />
          <span style={{ fontSize: 12, color: '#555' }}>No feed</span>
        </div>
      )}

      {/* Overlay: zone dot + cam name + badge */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '8px 8px', background: 'linear-gradient(to bottom, rgba(0,0,0,0.65), transparent)', display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: cam.zoneColor, flexShrink: 0, boxShadow: '0 0 4px rgba(0,0,0,0.4)' }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#fff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cam.name || cam.zoneName}</span>
        {mode === 'video' && (
          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#B12525', padding: '1px 5px', borderRadius: 4, letterSpacing: 0.5, animation: 'livePulse 2s ease-in-out infinite' }}>LIVE</span>
        )}
        {mode === 'snapshot' && (
          <span style={{ fontSize: 12, fontWeight: 600, color: '#fff', background: '#374151', padding: '1px 5px', borderRadius: 4, letterSpacing: 0.5 }}>SNAP</span>
        )}
      </div>
    </div>
  );
}

// ── Zone Live Cameras widget ──────────────────────────────────────
function ZoneLiveCameras({ zones }: { zones: GeoZone[] }) {
  const allCameras = buildCamList(zones);
  // Track IDs of cameras confirmed frozen/broken so we don't show them again
  const frozenIds = useRef(new Set<number>());
  const [pair, setPair] = useState<CamEntry[]>([]);

  function pickFiltered() {
    const available = allCameras.filter(c => !frozenIds.current.has(c.id));
    return pickTwo(available.length > 0 ? available : allCameras) as CamEntry[];
  }

  const cycle = useCallback(() => {
    setPair(pickFiltered());
  }, [allCameras.length]);

  // Pick initial pair + re-pick periodically
  useEffect(() => {
    if (allCameras.length === 0) return;
    setPair(pickFiltered());
    const delay = 20_000 + Math.random() * 10_000; // 20–30s
    const id = setInterval(() => setPair(pickFiltered()), delay);
    return () => clearInterval(id);
  }, [allCameras.length]);

  // Called by a panel when its stream is detected as frozen
  const handleFrozen = useCallback((camId: number) => {
    frozenIds.current.add(camId);
    // Immediately swap in a fresh pair excluding the frozen cam
    setPair(prev => {
      const available = allCameras.filter(c => !frozenIds.current.has(c.id));
      if (available.length === 0) return prev; // nothing better available
      // Replace only the frozen slot if possible
      return prev.map(c => c.id === camId
        ? (available.find(a => !prev.some(p => p.id === a.id)) ?? available[0])
        : c
      );
    });
  }, [allCameras]);

  if (allCameras.length === 0) return null;

  return (
    <div style={{ borderTop: '1px solid var(--color-neutral-100)', paddingTop: 10, marginTop: 8 }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
        <IconCamera size={12} color="var(--color-neutral-400)" />
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--color-neutral-400)', flex: 1 }}>
          Zone Cameras ({allCameras.length})
        </span>
        <button
          onClick={cycle}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-neutral-400)', display: 'flex', alignItems: 'center' }}
          title="Switch cameras"
        >
          <IconRefresh size={11} />
        </button>
      </div>

      {/* Camera panels */}
      <div style={{ display: 'flex', gap: 8 }}>
        {pair.map((cam, i) => (
          <LiveCameraPanel
            key={`${cam.id}-${i}`}
            cam={cam}
            onFrozen={() => handleFrozen(cam.id)}
          />
        ))}
      </div>

      {/* Keyframe injection (once per page load) */}
      <style>{`
        @keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}

function TimelineEntry({ entry }: { entry: LogEntry }) {
  const color = MODULE_COLORS[entry.module?.toLowerCase()] || MODULE_COLORS.system;
  const ini   = entry.actorName ? initials(entry.actorName) : 'S';
  return (
    <div className="id2-tl-entry">
      <div className="id2-tl-time">{formatTime(entry.created_at)}</div>
      <div className="id2-tl-avatar" style={{ background: color }}>{ini}</div>
      <div className="id2-tl-body">
        <div className="id2-tl-text">
          <strong>{entry.actorName || 'System'}</strong>{' '}
          {entry.action}{entry.details ? ` — ${entry.details}` : ''}
        </div>
        {entry.module && (
          <div className="id2-tl-tag" style={{ color, background: `${color}18` }}>
            {entry.module}
          </div>
        )}
      </div>
    </div>
  );
}

function PanelFallback() {
  return (
    <div className="id2-panel-fallback">
      <div className="id2-panel-fallback-dot" />
      <div className="id2-panel-fallback-dot" />
      <div className="id2-panel-fallback-dot" />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────

export default function IncidentDetail() {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const { incidents, activeIncident, setActiveIncident, hasRight } = useAuthStore();
  const { updateIncident, deleteIncident, isLoading }              = useIncidentStore();
  const { zones, fetchZones }                                       = useGeoOpsStore();

  const [activePanel,      setActivePanel]      = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMoreMenu,      setShowMoreMenu]     = useState(false);
  const [geoPoint,       setGeoPoint]       = useState<{ lat: number; lng: number } | null>(null);
  const [geoContext,     setGeoContext]     = useState<GeoContext | null>(null);
  const [weather,        setWeather]        = useState<WeatherSnap | null>(null);
  const [assessPins,     setAssessPins]     = useState<AssessPin[]>([]);
  const [showAssessPins, setShowAssessPins] = useState(true);

  // ── Display / EOC mode ───────────────────────────────────────
  const [displayMode,   setDisplayMode]   = useState(false);
  const [displayIdx,    setDisplayIdx]    = useState(0);
  const [displayPaused, setDisplayPaused] = useState(false);
  const [displayDwell,  setDisplayDwell]  = useState(30_000);

  const exitDisplay = useCallback(() => {
    setDisplayMode(false);
    setDisplayPaused(false);
    if (document.fullscreenElement) document.exitFullscreen?.();
  }, []);

  const enterDisplay = useCallback(() => {
    setDisplayMode(true);
    setDisplayIdx(0);
    setActivePanel(null);
    document.documentElement.requestFullscreen?.().catch(() => {});
  }, []);

  // Auto-advance tabs; restarted on every manual nav (displayIdx in deps resets the timer)
  useEffect(() => {
    if (!displayMode || displayPaused) return;
    const id = setInterval(() => {
      setDisplayIdx(i => {
        const next = (i + 1) % ALL_DISPLAY_TABS.length;
        setActivePanel(ALL_DISPLAY_TABS[next].id);
        return next;
      });
    }, displayDwell);
    return () => clearInterval(id);
  }, [displayMode, displayPaused, displayDwell, displayIdx]);

  // Escape key exits display mode (in addition to fullscreen Escape)
  useEffect(() => {
    if (!displayMode) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') exitDisplay(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [displayMode, exitDisplay]);

  const [stats, setStats] = useState<Stats>({
    shelters: 0, population: 0, capacity: 0,
    assessments: 0, assessmentsReview: 0,
    debrisLoads: 0, debrisOutstanding: 0,
    volunteers: 0, handoffEvents: 0,
    loaded: false,
  });
  const [timeline,     setTimeline]    = useState<LogEntry[]>([]);
  const [tlLoading,    setTlLoading]   = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  const incident = useMemo(
    () => incidents.find(i => String(i.id) === id),
    [incidents, id],
  );

  // Auto-set as active when viewed
  useEffect(() => {
    if (incident && activeIncident?.id !== incident.id) setActiveIncident(incident);
  }, [incident?.id]);

  // Load zones when incident changes
  useEffect(() => {
    if (incident) fetchZones();
  }, [incident?.id]);

  // Nominatim geocoding — captures address context too
  useEffect(() => {
    if (!incident) return;
    const lat = parseFloat((incident as unknown as Record<string, unknown>).latitude as string);
    const lng = parseFloat((incident as unknown as Record<string, unknown>).longitude as string);
    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) return;
    if (!incident.location) return;

    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(incident.location)}&format=json&limit=1&addressdetails=1`, {
      headers: { 'Accept-Language': 'en' },
    })
      .then(r => r.json())
      .then((data: { lat: string; lon: string; address?: Record<string, string> }[]) => {
        if (!data[0]) return;
        setGeoPoint({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
        const addr = data[0].address || {};
        setGeoContext({
          county: addr.county,
          city:   addr.city || addr.town || addr.village || addr.hamlet,
          state:  addr.state,
        });
      })
      .catch(() => {});
  }, [incident?.location]);

  // Open-Meteo weather — fires when incident or geocoded point is available
  useEffect(() => {
    if (!incident) return;
    const incRec2   = incident as unknown as Record<string, unknown>;
    const sLat = parseFloat(incRec2.latitude  as string);
    const sLng = parseFloat(incRec2.longitude as string);
    const hasS = !isNaN(sLat) && !isNaN(sLng) && sLat !== 0 && sLng !== 0;
    const lat  = hasS ? sLat : geoPoint?.lat ?? null;
    const lng  = hasS ? sLng : geoPoint?.lng ?? null;
    if (lat == null || lng == null) return;

    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
      `&current=temperature_2m,weathercode,windspeed_10m,wind_direction_10m` +
      `&daily=sunrise,sunset&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto&forecast_days=1`
    )
      .then(r => r.json())
      .then(d => {
        if (!d.current) return;
        setWeather({
          tempF:   Math.round(d.current.temperature_2m),
          code:    d.current.weathercode,
          windMph: Math.round(d.current.windspeed_10m),
          windDeg: d.current.wind_direction_10m,
          sunrise: d.daily?.sunrise?.[0] ?? '',
          sunset:  d.daily?.sunset?.[0]  ?? '',
        });
      })
      .catch(() => {});
  }, [incident?.id, geoPoint]);

  // Live data: stats + timeline
  useEffect(() => {
    if (!incident) return;
    const incId = String(incident.id);

    const loadStats = async () => {
      try {
        const FS   = FIELDS.shelters;
        const FSv  = FIELDS.survivors;
        const FIA  = FIELDS.impactAssessments;
        const FDLT = FIELDS.debrisLoadTickets;
        const FHE  = FIELDS.handoffEvents;

        const [sheltersRes, survivorsRes, assessRes, debrisRes, handoffRes, volRes] = await Promise.allSettled([
          queryRecords(TABLES.shelters,          { filters: [{ field: FS.incidentId,   operator: 'eq', value: incId }], limit: 500 }),
          queryRecords(TABLES.survivors,         { filters: [{ field: FSv.incidentId,  operator: 'eq', value: incId }], limit: 500 }),
          queryRecords(TABLES.impactAssessments, { filters: [{ field: FIA.incidentId,  operator: 'eq', value: incId }], limit: 500 }),
          queryRecords(TABLES.debrisLoadTickets, { filters: [{ field: FDLT.incidentId, operator: 'eq', value: incId }], limit: 500 }),
          queryRecords(TABLES.handoffEvents,     { filters: [{ field: FHE.incidentId,  operator: 'eq', value: incId }], limit: 200 }),
          queryRecords(TABLES.volunteers,        { limit: 500 }),
        ]);

        const shelterRecs = sheltersRes.status === 'fulfilled' ? sheltersRes.value.records : [];
        const survivorRecs = survivorsRes.status === 'fulfilled' ? survivorsRes.value.records : [];
        const assessRecs   = assessRes.status === 'fulfilled'   ? assessRes.value.records   : [];
        const debrisRecs   = debrisRes.status === 'fulfilled'   ? debrisRes.value.records   : [];
        const handoffRecs  = handoffRes.status === 'fulfilled'  ? handoffRes.value.records  : [];
        const volRecs      = volRes.status === 'fulfilled'      ? volRes.value.records      : [];

        const pop = shelterRecs.reduce((s, r) => s + (Number((r as Record<string, unknown>)[FS.currentPopulation]) || 0), 0);
        const cap = shelterRecs.reduce((s, r) => s + (Number((r as Record<string, unknown>)[FS.capacity]) || 0), 0);
        const reviewCount = assessRecs.filter(r => (r as Record<string, unknown>)[FIELDS.impactAssessments.status] === 'review').length;

        // Extract geocoded assessment pins
        const FIA2 = FIELDS.impactAssessments;
        const pins: AssessPin[] = [];
        for (const r of assessRecs) {
          const rr  = r as Record<string, unknown>;
          const lat = parseFloat(rr[FIA2.latitude]  as string);
          const lng = parseFloat(rr[FIA2.longitude] as string);
          if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
            pins.push({
              id:             r.record_id as number,
              lat, lng,
              address:        (rr[FIA2.address]        as string) || '',
              city:           (rr[FIA2.city]           as string) || '',
              damageCategory: (rr[FIA2.damageCategory] as string) || '',
            });
          }
        }
        setAssessPins(pins);
        const outstanding = debrisRecs.filter(r => {
          const s = String((r as Record<string, unknown>)[FIELDS.debrisLoadTickets.status] || '');
          return s === 'pending' || s === 'open' || s === '';
        }).length;

        setStats({
          shelters: shelterRecs.length, population: pop, capacity: cap,
          assessments: assessRecs.length, assessmentsReview: reviewCount,
          debrisLoads: debrisRecs.length, debrisOutstanding: outstanding,
          volunteers: volRecs.length,
          handoffEvents: handoffRecs.filter(r => (r as Record<string, unknown>)[FIELDS.handoffEvents.status] === 'active').length,
          loaded: true,
        });
      } catch { setStats(s => ({ ...s, loaded: true })); }
      finally  { setStatsLoading(false); }
    };

    const loadTimeline = async () => {
      setTlLoading(true);
      try {
        const FAL = FIELDS.activityLog;
        const res = await queryRecords(TABLES.activityLog, {
          filters: [{ field: FAL.incidentId, operator: 'eq', value: incId }],
          limit: 20,
          sort: { field: 'created_at', direction: 'desc' },
        });
        const entries: LogEntry[] = res.records.map((r: Record<string, unknown>) => ({
          id: r.record_id as number,
          created_at: (r.created_at as string) || '',
          actorName:  (r[FAL.actorName] as string) || 'System',
          module:     (r[FAL.module]    as string) || 'system',
          action:     (r[FAL.action]    as string) || '',
          details:    (r[FAL.details]   as string) || '',
        }));
        setTimeline(entries);
      } catch { /* silent */ }
      finally  { setTlLoading(false); }
    };

    loadStats();
    loadTimeline();
  }, [incident?.id]);

  // ── Derived values ──────────────────────────────────────────

  if (!incident) {
    return (
      <div className="id2-not-found">
        <IconAlertTriangle size={32} color="var(--color-neutral-400)" />
        <p>Incident not found.</p>
        <button className="id2-back-link" onClick={() => navigate('/incidents')}>
          <IconArrowLeft size={13} /> Back to incidents
        </button>
      </div>
    );
  }

  const incRec   = incident as unknown as Record<string, unknown>;
  const meta     = INCIDENT_TYPE_META[(incRec.incidentType as string || '').toLowerCase()] || INCIDENT_TYPE_META.other;
  const severity = ((incRec.severity as string) || '').toLowerCase();
  const sevColor = SEVERITY_COLOR[severity] || '#4B3FFF';
  const isActive = activeIncident?.id === incident.id;
  const elapsedStr = elapsed(incident.startDate);

  // Map coordinates — prefer stored lat/lng, fall back to Nominatim result
  const storedLat = parseFloat(incRec.latitude as string);
  const storedLng = parseFloat(incRec.longitude as string);
  const hasStored = !isNaN(storedLat) && !isNaN(storedLng) && storedLat !== 0 && storedLng !== 0;
  const mapLat = hasStored ? storedLat : geoPoint?.lat ?? null;
  const mapLng = hasStored ? storedLng : geoPoint?.lng ?? null;

  // Zones scoped to this incident
  const incidentZones = zones.filter(z => String(z.incidentId) === String(incident.id));

  // Active module panel definition
  const currentPanel = activePanel ? ALL_PANELS.find(p => p.id === activePanel) : null;

  const handleStatusChange = async (newStatus: IncidentStatus) => {
    try {
      await updateIncident(incident.id, { status: newStatus });
      notifications.show({ message: `Status updated to ${newStatus}`, color: 'var(--color-success)' });
    } catch (err) {
      notifications.show({ message: (err as Error)?.message || 'Failed to update status', color: 'var(--color-error)' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteIncident(incident.id);
      notifications.show({ message: 'Incident deleted', color: '#939598' });
      navigate('/incidents');
    } catch (err) {
      notifications.show({ message: (err as Error)?.message || 'Failed to delete', color: 'var(--color-error)' });
    }
  };

  const reloadTimeline = () => {
    setTlLoading(true);
    const incId = String(incident.id);
    const FAL = FIELDS.activityLog;
    queryRecords(TABLES.activityLog, {
      filters: [{ field: FAL.incidentId, operator: 'eq', value: incId }],
      limit: 20,
      sort: { field: 'created_at', direction: 'desc' },
    }).then(res => {
      const entries: LogEntry[] = res.records.map((r: Record<string, unknown>) => ({
        id: r.record_id as number,
        created_at: (r.created_at as string) || '',
        actorName:  (r[FAL.actorName] as string) || 'System',
        module:     (r[FAL.module]    as string) || 'system',
        action:     (r[FAL.action]    as string) || '',
        details:    (r[FAL.details]   as string) || '',
      }));
      const combined = entries.length < 5
        ? [...entries, ...DUMMY_EVENTS].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        : entries;
      setTimeline(combined);
    }).finally(() => setTlLoading(false));
  };

  return (
    <div className={`id2-page${displayMode ? ' id2-display-mode' : ''}`} onClick={() => showMoreMenu && setShowMoreMenu(false)}>

      {/* ── Breadcrumb ── */}
      <div className="id2-breadcrumb">
        <button className="id2-back-link" onClick={() => navigate('/incidents')}>
          <IconArrowLeft size={13} /> Back to incidents
        </button>
      </div>

      {/* ── Header card ── */}
      <div className="id2-header-card">
        <div className="id2-header-top">
          <div className="id2-header-badges">
            {severity && (
              <span className="id2-sev-badge" style={{ background: `${sevColor}18`, color: sevColor, borderColor: `${sevColor}30` }}>
                {severity.toUpperCase()}
              </span>
            )}
            <span className="id2-incident-num">INC-{incident.id}</span>
          </div>
          <div className="id2-header-actions">
            <button className="id2-btn-ghost" onClick={enterDisplay} title="EOC display mode">
              <IconPresentation size={14} /> Display
            </button>
            <button className="id2-btn-primary" onClick={() => navigate('/cop')}>
              <IconMap size={14} /> Open Live COP
            </button>
            {incident.status !== 'closed' && (
              <button className="id2-btn-danger" onClick={() => handleStatusChange('closed')}>
                End incident
              </button>
            )}
            {!isActive && (
              <button className="id2-btn-ghost" onClick={() => setActiveIncident(incident)}>
                <IconCheck size={14} /> Set active
              </button>
            )}
            <button className="id2-btn-ghost id2-btn-icon" onClick={() => navigate(`/incidents/${id}/edit`)}>
              <IconPencil size={14} />
            </button>
            <div className="id2-more-wrap" onClick={e => e.stopPropagation()}>
              <button className="id2-btn-ghost id2-btn-icon" onClick={() => setShowMoreMenu(v => !v)}>
                <IconDots size={14} />
              </button>
              {showMoreMenu && (
                <div className="id2-more-menu">
                  {(['active', 'monitoring', 'closed'] as IncidentStatus[]).map(s => (
                    <button key={s} className={`id2-menu-item${incident.status === s ? ' current' : ''}`}
                      onClick={() => { handleStatusChange(s); setShowMoreMenu(false); }}>
                      Set {s}
                    </button>
                  ))}
                  {hasRight?.('incidents', 'canDelete') && (
                    <>
                      <div className="id2-menu-sep" />
                      <button className="id2-menu-item danger"
                        onClick={() => { setShowDeleteConfirm(true); setShowMoreMenu(false); }}>
                        <IconTrash size={12} /> Delete incident
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <h1 className="id2-title">{incident.name}</h1>

        {(incident.state || incident.county || incident.city) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {incident.state && (
              <span style={{ fontSize: 12, fontWeight: 600, background: '#EEF2FF', color: '#4B3FFF', borderRadius: 4, padding: '3px 8px', border: '1px solid #C7D2FE' }}>
                {incident.state}
              </span>
            )}
            {incident.county?.split('|').filter(Boolean).map(c => (
              <span key={c} style={{ fontSize: 12, background: '#F0FDF4', color: '#166534', borderRadius: 4, padding: '3px 8px', border: '1px solid #BBF7D0' }}>
                {c}
              </span>
            ))}
            {incident.city?.split('|').filter(Boolean).map(c => (
              <span key={c} style={{ fontSize: 12, background: '#F8F8F8', color: '#475569', borderRadius: 4, padding: '3px 8px', border: '1px solid #E2E8F0' }}>
                {c}
              </span>
            ))}
          </div>
        )}

        <div className="id2-meta">
          {incident.startDate && (
            <span>
              <IconClock size={12} />
              Started {new Date(incident.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
          {elapsedStr && <span className="id2-elapsed">{elapsedStr}</span>}
          {incident.commander && <span><IconUsers size={12} /> {incident.commander}</span>}
          {incident.location   && <span><IconMapPin size={12} /> {incident.location}</span>}
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className="id2-stats-bar">
        <StatCell label="Shelters"       Icon={IconHomePlus}       color="#0EA5E9" value={stats.shelters}      sub={stats.capacity > 0 ? `${stats.population} of ${stats.capacity} cap` : `${stats.population} residents`} loading={statsLoading} />
        <StatCell label="Survivors"      Icon={IconUsers}           color="#7c3aed" value={stats.population}    sub={stats.capacity > 0 ? `of ${stats.capacity} cap` : 'displaced'}                                          loading={statsLoading} />
        <StatCell label="Damage Reports" Icon={IconHomeExclamation} color="#f59e0b" value={stats.assessments}   sub={stats.assessmentsReview > 0 ? `${stats.assessmentsReview} to review` : 'all reviewed'}                  loading={statsLoading} />
        <StatCell label="Debris Loads"   Icon={IconBackhoe}         color="#f97316" value={stats.debrisLoads}   sub={stats.debrisOutstanding > 0 ? `${stats.debrisOutstanding} outstanding` : 'all closed'}                  loading={statsLoading} />
        <StatCell label="Volunteers"     Icon={IconUserPlus}        color="#10b981" value={stats.volunteers}    sub="registered"                                                                                              loading={statsLoading} />
        <StatCell label="Handoffs"       Icon={IconArrowsExchange}  color="#4B3FFF" value={stats.handoffEvents} sub="active shifts"                                                                                           loading={statsLoading} />
      </div>

      {/* ── Tab bar ── */}
      <div className="id2-tabs-row" style={{
        display: 'flex', alignItems: 'center',
        borderBottom: '1px solid var(--color-neutral-200)',
        marginBottom: 'var(--spacing-4)',
      }}>
        {[
          { id: null, name: 'Overview', Icon: IconLayoutDashboard },
          ...RESPOND_PANELS,
          ...RECOVER_PANELS,
        ].map(p => (
          <button
            key={p.id ?? '__overview'}
            className={`tab${activePanel === p.id ? ' active' : ''}`}
            onClick={() => setActivePanel(p.id)}
            style={{ flex: 1, justifyContent: 'center', margin: 0 }}
          >
            <p.Icon size={14} />
            {p.name}
          </button>
        ))}
      </div>

      {/* ── Panel area ── */}
      {activePanel === null ? (

        /* ── Default: Incident overview ── */
        <>
        <div className="id2-body">

          {/* Left: map fills full card height */}
          <div className="id2-map-col">
            <div className="id2-map-card id2-panel">
              <div className="id2-map-leaflet-box">
                <IncidentMap
                  lat={mapLat} lng={mapLng} zones={incidentZones}
                  assessPins={assessPins} showAssessPins={showAssessPins}
                  stateAbbr={incident.state}
                  countyNames={incident.county ? incident.county.split('|').filter(Boolean) : []}
                />
                {incidentZones.length > 0 && (
                  <div className="id2-map-zone-badge">{incidentZones.length} GeoOps zone{incidentZones.length !== 1 ? 's' : ''}</div>
                )}
                {/* Layer toggles */}
                <div className="id2-map-layers">
                  {assessPins.length > 0 && (
                    <button
                      className={`id2-map-layer-btn${showAssessPins ? ' active' : ''}`}
                      onClick={() => setShowAssessPins(v => !v)}
                    >
                      <span className="id2-map-layer-dot" style={{ background: '#B12525' }} />
                      Impact ({assessPins.length})
                    </button>
                  )}
                </div>
                <button className="id2-map-open" onClick={() => navigate('/geoops')}>
                  <IconExternalLink size={11} /> Open in GeoOps
                </button>
              </div>
              {/* ── Geo info panel ── */}
              <div className="id2-geo-panel">

                {/* Row 1: location context + weather */}
                <div className="id2-geo-row">
                  {geoContext && (
                    <span className="id2-geo-fact">
                      📍 {[geoContext.city, geoContext.county, geoContext.state].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {weather && (() => {
                    const w = wmoLabel(weather.code);
                    return (
                      <>
                        <span className="id2-geo-sep" />
                        <span className="id2-geo-fact">
                          {w.emoji} {weather.tempF}°F · {w.label} · {weather.windMph} mph {windCardinal(weather.windDeg)}
                        </span>
                        {weather.sunset && (
                          <>
                            <span className="id2-geo-sep" />
                            <span className="id2-geo-fact id2-geo-daylight">
                              🌅 {daylightStatus(weather.sunrise, weather.sunset)}
                            </span>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Row 2: zone cards */}
                {incidentZones.length > 0 && (
                  <div className="id2-geo-zones">
                    {Object.entries(
                      incidentZones.reduce<Record<string, GeoZone[]>>((acc, z) => {
                        (acc[z.zoneType] ??= []).push(z);
                        return acc;
                      }, {})
                    ).map(([type, zs]) => {
                      const color = ZONE_TYPE_META[type]?.color || '#6B7280';
                      return (
                        <div key={type} className="id2-zone-card" style={{ borderColor: `${color}40` }}>
                          <div className="id2-zone-card-header" style={{ background: `${color}12` }}>
                            <span className="id2-zone-card-dot" style={{ background: color }} />
                            <span className="id2-zone-card-type">{ZONE_TYPE_META[type]?.label || type}</span>
                            <span className="id2-zone-card-count">{zs.length}</span>
                          </div>
                          <div className="id2-zone-card-names">
                            {zs.slice(0, 3).map(z => (
                              <span key={z.id} className="id2-zone-card-name">{z.name}</span>
                            ))}
                            {zs.length > 3 && (
                              <span className="id2-zone-card-more">+{zs.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Row 3: zone cameras */}
                <ZoneLiveCameras zones={incidentZones} />
              </div>
            </div>
          </div>

          {/* Right sidebar: situation + quick info — height drives map height */}
          <div className="id2-sidebar">
            <div className="id2-panel">
              {incident.description && (
                <>
                  <div className="id2-panel-header">
                    <span className="id2-panel-title">SITUATION</span>
                  </div>
                  <div className="id2-qi-grid" style={{ borderBottom: '1px solid var(--color-neutral-100)', paddingBottom: 16, marginBottom: 4 }}>
                    <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--color-neutral-700)', margin: 0 }}>
                      {incident.description}
                    </p>
                  </div>
                </>
              )}

              <div className="id2-panel-header" style={{ borderTop: incident.description ? '1px solid var(--color-neutral-100)' : undefined }}>
                <span className="id2-panel-title">QUICK INFO</span>
                <button className="id2-panel-edit" onClick={() => navigate(`/incidents/${id}/edit`)}>
                  <IconPencil size={12} /> Edit
                </button>
              </div>
              <div className="id2-qi-grid">
                <div className="id2-qi-row">
                  <span className="id2-qi-label">STATUS</span>
                  <span className="id2-qi-val" style={{ textTransform: 'capitalize' }}>
                    <span className={`id2-status-dot id2-status-dot--${incident.status}`} />
                    {incident.status}
                  </span>
                </div>
                <div className="id2-qi-row">
                  <span className="id2-qi-label">TYPE</span>
                  <span className="id2-qi-val">{meta.label}</span>
                </div>
                <div className="id2-qi-row">
                  <span className="id2-qi-label">SEVERITY</span>
                  <span className="id2-qi-val" style={{ color: sevColor, fontWeight: 600 }}>
                    {severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : '—'}
                  </span>
                </div>
                {incident.location && (
                  <div className="id2-qi-row">
                    <span className="id2-qi-label">LOCATION</span>
                    <span className="id2-qi-val">{incident.location}</span>
                  </div>
                )}
                {incident.commander && (
                  <div className="id2-qi-row">
                    <span className="id2-qi-label">IC</span>
                    <span className="id2-qi-val">{incident.commander}</span>
                  </div>
                )}
                <div className="id2-qi-row">
                  <span className="id2-qi-label">DECLARED BY</span>
                  <span className="id2-qi-val">{incident.createdByName || '—'}</span>
                </div>
                {incident.startDate && (
                  <div className="id2-qi-row">
                    <span className="id2-qi-label">STARTED</span>
                    <span className="id2-qi-val">{new Date(incident.startDate).toLocaleDateString()}</span>
                  </div>
                )}
                {elapsedStr && (
                  <div className="id2-qi-row">
                    <span className="id2-qi-label">ELAPSED</span>
                    <span className="id2-qi-val" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{elapsedStr}</span>
                  </div>
                )}
                {/* Status change shortcuts */}
                <div className="id2-qi-row" style={{ marginTop: 8 }}>
                  <span className="id2-qi-label">CHANGE</span>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {(['active', 'monitoring', 'closed'] as IncidentStatus[]).map(s => (
                      <button key={s}
                        disabled={incident.status === s || isLoading}
                        onClick={() => handleStatusChange(s)}
                        className={`id2-ov-status-btn${incident.status === s ? ' current' : ''}`}
                        style={{ fontSize: 12, height: 24, padding: '0 8px' }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Jurisdiction — counties & cities */}
              {(incident.state || incident.county || incident.city) && (
                <>
                  <div className="id2-panel-header" style={{ borderTop: '1px solid var(--color-neutral-100)' }}>
                    <span className="id2-panel-title">JURISDICTION</span>
                  </div>
                  <div className="id2-jur-grid">
                    {incident.state && (
                      <div className="id2-jur-row">
                        <span className="id2-qi-label">STATE</span>
                        <span className="id2-jur-chip id2-jur-chip--state">{incident.state}</span>
                      </div>
                    )}
                    {incident.county && (
                      <div className="id2-jur-row">
                        <span className="id2-qi-label">COUNTIES</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {incident.county.split('|').filter(Boolean).map(c => (
                            <span key={c} className="id2-jur-chip id2-jur-chip--county">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {incident.city && (
                      <div className="id2-jur-row">
                        <span className="id2-qi-label">CITIES</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {incident.city.split('|').filter(Boolean).map(c => (
                            <span key={c} className="id2-jur-chip id2-jur-chip--city">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Timeline — full width below the map+sidebar row */}
        <div className="id2-panel">
          <div className="id2-panel-header">
            <span className="id2-panel-title">INCIDENT TIMELINE</span>
            <div className="id2-panel-actions">
              <span className="id2-tl-sub">chronological log</span>
              <button className="id2-tl-btn" onClick={reloadTimeline}>
                <IconRefresh size={12} />
              </button>
            </div>
          </div>
          <div className="id2-timeline-list" style={{ maxHeight: 340 }}>
            {tlLoading ? (
              [1, 2, 3, 4].map(i => (
                <div key={i} className="id2-tl-entry">
                  <div className="id2-tl-time"><div className="id2-tl-skel" style={{ width: 36 }} /></div>
                  <div className="id2-tl-avatar id2-tl-skel-avatar" />
                  <div className="id2-tl-body"><div className="id2-tl-skel id2-tl-skel-text" /></div>
                </div>
              ))
            ) : timeline.length > 0 ? (
              timeline.map(entry => <TimelineEntry key={entry.id} entry={entry} />)
            ) : (
              <div className="id2-tl-empty">
                <IconActivity size={24} color="var(--color-neutral-300)" />
                <p>No activity logged yet. Events will appear here as they happen across modules.</p>
              </div>
            )}
          </div>
        </div>
        </>

      ) : (

        /* ── Module panel ── */
        <div className="id2-module-panel">
          <Suspense fallback={<PanelFallback />}>
            {currentPanel && <currentPanel.Component />}
          </Suspense>
        </div>

      )}

      {/* ── EOC Display HUD ── */}
      {displayMode && (
        <div className="id2-display-hud">
          {/* Progress bar */}
          <div
            key={`${displayIdx}-${displayPaused}`}
            className={`id2-display-progress${displayPaused ? ' paused' : ''}`}
            style={{ animationDuration: `${displayDwell}ms` }}
          />

          {/* Nav controls */}
          <div className="id2-display-nav">
            <button className="id2-display-ctrl" onClick={() => {
              const prev = (displayIdx - 1 + ALL_DISPLAY_TABS.length) % ALL_DISPLAY_TABS.length;
              setDisplayIdx(prev);
              setActivePanel(ALL_DISPLAY_TABS[prev].id);
            }}><IconChevronLeft size={14} /></button>
            <button className="id2-display-ctrl" onClick={() => setDisplayPaused(p => !p)}>
              {displayPaused ? <IconPlayerPlay size={14} /> : <IconPlayerPause size={14} />}
            </button>
            <button className="id2-display-ctrl" onClick={() => {
              const next = (displayIdx + 1) % ALL_DISPLAY_TABS.length;
              setDisplayIdx(next);
              setActivePanel(ALL_DISPLAY_TABS[next].id);
            }}><IconChevronRight size={14} /></button>
          </div>

          {/* Current tab info */}
          <div className="id2-display-info">
            <span className="id2-display-incident">{incident.name}</span>
            <span className="id2-display-sep">·</span>
            <span className="id2-display-tab">{ALL_DISPLAY_TABS[displayIdx].name}</span>
            <span className="id2-display-counter">{displayIdx + 1} / {ALL_DISPLAY_TABS.length}</span>
          </div>

          {/* Dwell selector */}
          <div className="id2-display-dwell">
            {[15, 30, 60].map(s => (
              <button
                key={s}
                className={`id2-display-dwell-btn${displayDwell === s * 1000 ? ' active' : ''}`}
                onClick={() => setDisplayDwell(s * 1000)}
              >{s}s</button>
            ))}
          </div>

          {/* Exit */}
          <button className="id2-display-exit" onClick={exitDisplay}>
            <IconX size={14} /> Exit
          </button>
        </div>
      )}

      {/* ── Delete confirmation ── */}
      {showDeleteConfirm && (
        <div className="id2-modal-backdrop" onClick={() => setShowDeleteConfirm(false)}>
          <div className="id2-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete incident?</h3>
            <p>This will permanently remove <strong>{incident.name}</strong> and all associated data. This cannot be undone.</p>
            <div className="id2-modal-actions">
              <button className="id2-modal-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="id2-modal-delete" onClick={handleDelete} disabled={isLoading}>
                {isLoading ? 'Deleting…' : 'Delete incident'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
